/* eslint-disable react-compiler/react-compiler */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import useTableStore from '@/data/store/useTableStore';
import usePlatform from '@/utils/hooks/usePlatform';
import Checkbox from '@/components/General/inputs/Checkbox';
import Unicon from '../Unicon';
import { getDefaultSortDirection } from './sortUtils';
import GuideTarget from '@/components/Guides/GuideTarget';

// Module-scope memoized cell — declaring it inside TableHeader's render created
// a new component type each render, forcing React to unmount/remount every
// header cell (memo useless, cell state/DOM lost on web). Closure-dependent
// values are passed as props instead.
const HeaderCell = React.memo(({ column, index, tableId, tableWidth, columnCount, sortDirection, onSort, onStartDrag }) => {
    const width = useTableStore(React.useCallback((s) => s.columnWidths[tableId]?.[index], [tableId, index]));
    const computedWidth = Math.max(width || column.width || (tableWidth / columnCount), 150);
    const isSortable = column.sortable === true;

    return (
        <Pressable
            testID={`TableHeader-${tableId}-${column.accessor || index}`}
            onPress={() => onSort(column)}
            disabled={!isSortable}
            style={styles.headerCell(computedWidth, column.minWidth, isSortable, sortDirection)}
        >
            <View style={styles.headerCellContent}>
                <Text style={styles.headerText} numberOfLines={3}>{column.value}</Text>
                {isSortable && (
                    <View style={styles.sortIconContainer}>
                        {sortDirection === 'asc' ? (
                            <Unicon name="sort-amount-down-alt" size={16} library="fa5" colorVariant="text" />
                        ) : sortDirection === 'desc' ? (
                            <Unicon name="sort-amount-up" size={16} library="fa5" colorVariant="text" />
                        ) : (
                            <Unicon name="sort" size={16} library="fa5" colorVariant="textSecondary" />
                        )}
                    </View>
                )}
            </View>
            <Pressable onPointerDown={(e) => onStartDrag(e, column, index, tableId, tableWidth)} style={styles.columnDrag} />
        </Pressable>
    );
});

const TableHeader = ({ columns, tableId, tableWidth, hasActions = false, batchSelection, isAllSelected, isIndeterminate, onSelectAll, sortConfig, onSortChange, guideSelectionTarget, guideSortTarget }) => {
    const [dragging, setDragging] = React.useState(false);
    const {isWeb} = usePlatform();
    // Only select the updater to avoid rerendering parent on every width change
    const updateColumnWidth = useTableStore(store => store.updateColumnWidth);

    // Refs to avoid stale closures and to keep current drag context
    const draggingColRef = useRef(null);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);
    const widthsRef = useRef(useTableStore.getState().columnWidths);
    const tableIdRef = useRef(tableId);
    // Keep the latest widths in a ref without causing rerenders
    useEffect(() => {
        const unsub = useTableStore.subscribe((state) => {
            widthsRef.current = state.columnWidths;
        });
        return unsub;
    }, []);

    // Keep latest tableId for updates
    useEffect(() => {
        tableIdRef.current = tableId;
    }, [tableId]);

    // rAF throttle
    const rafIdRef = useRef(0);
    const pendingColRef = useRef(null);
    const pendingWidthRef = useRef(0);

    const flushWidthUpdate = useCallback(() => {
        rafIdRef.current = 0;
        if (pendingColRef.current != null) {
            updateColumnWidth(tableIdRef.current, pendingColRef.current, pendingWidthRef.current);
        }
    }, [updateColumnWidth]);

    const onPointerMove = useCallback((e) => {
        const activeCol = draggingColRef.current;
        if (activeCol == null || activeCol == undefined || !columns[activeCol]) return;

        const columnCount = columns.length + (hasActions ? 1 : 0);
        const clientX = e?.nativeEvent?.clientX ?? e.clientX;
        if (typeof clientX !== 'number') return;

        const delta = clientX - startXRef.current;
        const nextWidth = Math.max(columns[activeCol].minWidth || (tableWidth / columnCount) || 150, startWidthRef.current + delta);

        // Schedule an update on the next animation frame
        pendingColRef.current = activeCol;
        pendingWidthRef.current = nextWidth;
        if (!rafIdRef.current) {
            rafIdRef.current = requestAnimationFrame(flushWidthUpdate);
        }
    }, [flushWidthUpdate, columns, tableWidth]);

    const onPointerUp = useCallback(() => {
        if (draggingColRef.current == null || draggingColRef.current == undefined) return;
        draggingColRef.current = null;
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            flushWidthUpdate();
        }
        setDragging(false);
    }, [flushWidthUpdate]);

    useEffect(() => {
        if(isWeb){
            if (dragging) {
                document.addEventListener('pointermove', onPointerMove);
                document.addEventListener('pointerup', onPointerUp);
                if (document?.body) {
                    document.body.style.userSelect = 'none';
                }
            } else {
                document.removeEventListener('pointermove', onPointerMove);
                document.removeEventListener('pointerup', onPointerUp);
                if (document?.body) {
                    document.body.style.userSelect = '';
                }
            }
            return () => {
                document.removeEventListener('pointermove', onPointerMove);
                document.removeEventListener('pointerup', onPointerUp);
                if (document?.body) {
                    document.body.style.userSelect = '';
                }
            };
        }
    }, [dragging, onPointerMove, onPointerUp])

    const onStartDrag = useCallback((e, column, index, tableId, tableWidth) => {
        const columnCount = columns.length + (hasActions ? 1 : 0);
        // Initialize drag context
        draggingColRef.current = index;
        const clientX = e?.nativeEvent?.clientX ?? e.clientX;
        startXRef.current = typeof clientX === 'number' ? clientX : 0;
        startWidthRef.current = widthsRef.current[tableId]?.[index] || column.width || (tableWidth / columnCount);
        setDragging(true);
        // Prevent text selection while dragging
        if (typeof e?.preventDefault === 'function') e.preventDefault();
    }, []);

    const handleSort = useCallback((column) => {
        if (!column.sortable || !onSortChange) return;
        
        const accessor = column.accessor;
        const defaultDirection = getDefaultSortDirection(column);
        let newDirection = defaultDirection;
        
        if (sortConfig?.key === accessor) {
            newDirection = sortConfig.direction === defaultDirection
                ? (defaultDirection === 'asc' ? 'desc' : 'asc')
                : null;
        }
        
        onSortChange(newDirection ? { key: accessor, direction: newDirection } : null);
    }, [sortConfig, onSortChange]);

    // First sortable column doubles as the tour's sort target; activate runs
    // the same handleSort the header press uses.
    const columnCount = columns.length + (hasActions ? 1 : 0);
    const firstSortableIndex = useMemo(() => columns.findIndex(column => column.sortable), [columns]);
    const columnComponents = useMemo(() => columns.map((column, index) => {
        const sortDirection = sortConfig && sortConfig.key === column.accessor ? sortConfig.direction : null;
        const cell = <HeaderCell key={index} column={column} index={index} tableId={tableId} tableWidth={tableWidth} columnCount={columnCount} sortDirection={sortDirection} onSort={handleSort} onStartDrag={onStartDrag} />;
        return index === firstSortableIndex && guideSortTarget
            ? <GuideTarget key={index} id={guideSortTarget} activate={onSortChange ? () => handleSort(column) : undefined}>{cell}</GuideTarget>
            : cell;
    }), [columns, tableId, tableWidth, columnCount, sortConfig, firstSortableIndex, guideSortTarget, handleSort, onSortChange, onStartDrag])

    const SelectionView = guideSelectionTarget ? GuideTarget : View;
    return <View style={styles.container}>
        {batchSelection && (
            <SelectionView id={guideSelectionTarget} {...(guideSelectionTarget ? { activate: onSelectAll } : {})} style={styles.checkboxCell}>
                <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={onSelectAll}
                    size={18}
                />
            </SelectionView>
        )}
        {columnComponents}
        {hasActions && <HeaderCell key={columns.length} column={{value:"Actions"}} index={columns.length} tableId={tableId} tableWidth={tableWidth} columnCount={columnCount} sortDirection={null} onSort={handleSort} onStartDrag={onStartDrag} />}
    </View>
};

const styles = StyleSheet.create((theme) => ({
    container: {
        borderBottomWidth:1,
        borderBottomColor:theme.colors.borderSubtle,
        backgroundColor: theme.colors.surfaceVariant,
        flexDirection: 'row',
        zIndex: 100,
    },
    checkboxCell: {
        width: 50,
        paddingVertical: 10,
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerCell: (width, minWidth, isSortable, sortDirection) => ({
        width: width,
        flex: width ? undefined : 1,
        minWidth: minWidth || undefined,
        paddingHorizontal: 20,
        paddingVertical: 10,
        justifyContent: 'center',
        backgroundColor: sortDirection ? theme.colors.hover : 'transparent',
        _web: {
            cursor: isSortable ? 'pointer' : 'default',
            transition: 'background-color 0.1s ease-in-out',
            ...(isSortable && {
                _hover: {
                    backgroundColor: theme.colors.hover
                }
            })
        }
    }),
    headerCellContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    headerText: {
        flexShrink: 1,
        minWidth: 0,
        fontSize: 13,
        fontFamily: theme.fonts.heading,
        color: theme.colors.textSecondary,
    },
    sortIconContainer: {
        paddingLeft:4,
        opacity: 0.7,
        flexShrink: 0,
    },
    columnDrag: {
        width: 12,
        backgroundColor: "transparent",
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
        _web: {
            cursor: 'col-resize'
        }
    }
}));

export default React.memo(TableHeader);